import React, { useRef, useState, useContext, useEffect } from 'react';

import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import { UserDataContext } from 'lane-shared/contexts';
import { EssensysIntegrationSetupError } from 'activate-errors';
import { isThisError } from 'lane-shared/helpers';
import { getPaymentFeatureCurrency } from 'lane-shared/helpers/features';
import { getCachedEssensysReservableValue } from 'lane-shared/helpers/integrations/Essensys';
import { usePaymentAndMenuFeatureConfirmation } from 'lane-shared/hooks';
import useOperateAccountContactPairs from 'lane-shared/hooks/integrations/essensys/useOperateAccountContactPairs';
import { AccountContactPair } from 'lane-shared/types/Essensys';
import { FeatureNameEnum } from 'constants-content';

import { CurrencyInput, Toggle } from 'components/form';
import { Button, ErrorMessage, Loading } from 'components/general';
import { Modal } from 'components/lds';
import PlacePayPaymentModal from 'components/payments/PlacePayPaymentModal';

import PaymentFeatureQuote from '../../PaymentFeatureQuote';
import PaymentFeatureConfirmationModalProps from '../PaymentFeatureConfirmationModalProps';
import Home from '../screens/Home';
import AccountForm from './AccountForm';
import LinkedAccounts from './LinkedAccounts';
import PaymentMethodContent from './PaymentMethodContent';
import {
  ModalHistoryMapType,
  MODAL_HISTORY_EVENT_TYPES,
} from './types/modalHistoryTypes';
import useUserLocale from 'hooks/useUserLocale';

import styles from '../PaymentFeatureConfirmationModal.scss';

const ALL_LOCATIONS = undefined;

export default function EssensysPosPaymentFeatureConfirmationModal({
  isOpen = false,
  onClose = () => undefined,
  onPaymentSuccess = () => undefined,
  onPaymentFailed = () => undefined,
  onPaymentFeatureUpdated = () => undefined,
  onBeforeSubmit = () => undefined,
  content,
  interaction,
}: PaymentFeatureConfirmationModalProps) {
  const cachedEssensysReservableValue =
    getCachedEssensysReservableValue()?.reservation;
  const hasNullStartEndDate =
    interaction?.features?.Reservable?.reservation.startDate === null &&
    interaction?.features?.Reservable?.reservation.endDate === null;

  if (hasNullStartEndDate) {
    interaction = {
      ...interaction,
      features: {
        ...interaction?.features,
        Reservable: {
          ...interaction?.features?.Reservable,
          reservation: cachedEssensysReservableValue,
        },
      },
    };
  }

  const locale = useUserLocale();
  const { t } = useTranslation();
  const { user } = useContext(UserDataContext);
  const [modalHistory, setModalHistory] = useState<MODAL_HISTORY_EVENT_TYPES[]>(
    []
  );
  const placePayRef = useRef<{ reject: Function; resolve: Function }>({
    reject: () => undefined,
    resolve: () => undefined,
  });
  const [isPlacePayOpen, setIsPlacePayOpen] = useState(false);

  const userId = interaction?.user?._id || user?._id;

  const {
    accountContactPairs,
    accountContactPair,
    createAccountContactPair,
    selectAccountContactPair,
    loading: accountContactPairsLoading,
  } = useOperateAccountContactPairs(ALL_LOCATIONS, { skip: false });

  const {
    error,
    loading: paymentMenuFeatureLoading,
    essensysError,
    essensysPaymentSettings,
    submitting,
    selectedCard,
    setSelectedCard,
    paymentAccounts,
    selectedPaymentAccount,
    isStripePaymentAccount,
    setSelectedPaymentAccount,
    loadingQuote,
    paymentFeature,
    paymentDetails,
    disabled,
    quote,
    onSubmitInteraction,
    paymentType,
    essensysLoading,
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '{ contactId: string | undefined;... Remove this comment to see the full error message
  } = usePaymentAndMenuFeatureConfirmation({
    contactId: accountContactPair?.contact.contactid,
    locale,
    content,
    interaction,
    userId,
    onBeforeSubmit,
    onPaymentFailed,
    onPaymentSuccess,
    onBeforePlacePayPayment: async () =>
      new Promise((resolve, reject) => {
        setIsPlacePayOpen(true);
        placePayRef.current.resolve = resolve;
        placePayRef.current.reject = reject;
      }),
  });

  useEffect(() => {
    if (submitting && isOpen && !isPlacePayOpen) {
      const EXTRA_LONG_DURATION_TO_OVERIDE_DEFAULT = 100000;

      window.Toast.show(
        'Finalizing your purchase. Please do not refresh page',
        EXTRA_LONG_DURATION_TO_OVERIDE_DEFAULT
      );

      return;
    }

    window.Toast.hide();
  }, [submitting, isPlacePayOpen, isOpen]);

  if (!content || !interaction || !paymentFeature) {
    return null;
  }

  const handleBack = () => {
    setModalHistory(prevModalHistory => {
      if (modalHistory.length > 0) {
        return prevModalHistory.slice(0, -1);
      }

      return [];
    });
  };

  // get any applicable rules for this team role
  const applicableRules =
    paymentFeature?.rules?.filter(rule =>
      user?.roles?.some(role => role.groupRole?._id === rule.groupRole?._id)
    ) || [];

  const canOverridePrice = applicableRules.some(rule => rule.canOverridePrice);

  // Submit on behalf of logic
  const userName =
    interaction?.features?.[FeatureNameEnum.SubmitOnBehalfOf]?.user?.name || '';

  const hideError =
    isThisError(error, EssensysIntegrationSetupError) && !accountContactPair;

  const loading =
    paymentMenuFeatureLoading ||
    accountContactPairsLoading ||
    loadingQuote ||
    !quote ||
    essensysLoading;

  // TODO Ensure this is working for the Flex Member case (Credits) w/ missing payeeid
  const confirmDisabled =
    disabled ||
    !(quote as any)?.data ||
    (essensysPaymentSettings &&
      !essensysPaymentSettings?.orgpayeeid &&
      !isStripePaymentAccount) ||
    loading ||
    !selectedPaymentAccount?._id;

  const modalContent = (
    <>
      {error && !hideError && <ErrorMessage error={error} />}

      {loadingQuote ? (
        <Loading />
      ) : (
        <PaymentFeatureQuote quote={quote || undefined} content={content} />
      )}

      {canOverridePrice && (
        <div className={styles.override}>
          <Toggle
            value={!!interaction?.features?.Payment?.overridePrice}
            onChange={() =>
              onPaymentFeatureUpdated({
                overridePrice: !interaction?.features?.Payment?.overridePrice,
                overriddenAmount: quote?.total,
                amount: quote?.total,
              })
            }
            text={t('Override price')}
            doTranslate
          />
          <div className={styles.spacer} />
          {interaction?.features?.Payment?.overridePrice && (
            <CurrencyInput
              key="currency-input"
              value={interaction?.features?.Payment?.amount || 0}
              currency={getPaymentFeatureCurrency(paymentFeature)}
              onValueChange={amount =>
                onPaymentFeatureUpdated({
                  amount,
                })
              }
            />
          )}
        </div>
      )}

      <PaymentMethodContent
        essensysPaymentSettings={essensysPaymentSettings}
        essensysError={essensysError}
        // @ts-expect-error ts-migrate(2322) FIXME: Type 'PaymentTypeEnum | null' is not assignable to... Remove this comment to see the full error message
        paymentType={paymentType}
        // @ts-expect-error ts-migrate(2322) FIXME: Type 'FeatureQuoteType | PaymentFeatureQuoteType |... Remove this comment to see the full error message
        quote={quote}
        selectedCard={selectedCard}
        // @ts-expect-error ts-migrate(2322) FIXME: Type 'PaymentDetailsType | null' is not assignable... Remove this comment to see the full error message
        paymentDetails={paymentDetails}
        paymentAccounts={paymentAccounts}
        selectedPaymentAccount={selectedPaymentAccount}
        setSelectedPaymentAccount={setSelectedPaymentAccount}
        setSelectedCard={setSelectedCard}
        onClose={onClose}
      />
    </>
  );

  const hasAccountContactPairs = Boolean(accountContactPairs?.length);

  const suggestedCompanyName = accountContactPairs?.[0]?.account.clientname;

  const modalHistoryMap: ModalHistoryMapType = {
    default: {
      title: t('Confirm your purchase'),
      component: Home,
      componentProps: {
        className: cx(styles.modalContainer),
        children: modalContent,
        userName,
        loading,
        essensysPaymentSettings,
        setModalHistory,
        accountContactPair,
        accountContactPairs,
        isStripePaymentAccount,
      },
      actions: () => (
        <Button
          fullWidth
          variant="contained"
          onClick={onSubmitInteraction}
          disabled={confirmDisabled}
        >
          {t(!submitting ? 'Purchase' : 'In progress...')}
        </Button>
      ),
    },
    linkedAccounts: {
      title: t('Linked accounts'),
      component: LinkedAccounts,
      componentProps: {
        className: cx(styles.modalContainer),
        contentId: content._id,
        accountContactPair,
        accountContactPairs,
        onAccountContactPairSelect: (
          accountContactPair: AccountContactPair
        ) => {
          selectAccountContactPair(accountContactPair);
          setModalHistory([]);
        },
        onAddNewAccountClick: () =>
          setModalHistory(prev => [
            ...prev,
            MODAL_HISTORY_EVENT_TYPES.CONFIRM_YOUR_ACCOUNT_INFO,
          ]),
      },
    },
    confirmYourAccountInfo: {
      title: hasAccountContactPairs
        ? t('Confirm your account info')
        : t('Add a new account'),
      component: AccountForm,
      componentProps: {
        className: cx(styles.modalContainer),
        buttonLabel: hasAccountContactPairs ? t('Confirm') : t('Add'),
        createAccountContactPair: async (
          companyName: string,
          email: string
        ) => {
          await createAccountContactPair(companyName, email, content._id);
          setModalHistory([]);
        },
        defaultCompanyName: suggestedCompanyName,
      },
    },
  };

  const currentHistory =
    modalHistory[modalHistory.length - 1] ?? MODAL_HISTORY_EVENT_TYPES.DEFAULT;
  const {
    component: CurrentComponent,
    title,
    actions: CurrentActions,
    componentProps,
  } = modalHistoryMap[currentHistory];

  return (
    <>
      <Modal
        isOpen={isOpen}
        onBack={handleBack}
        modalHistory={modalHistory}
        onClose={onClose}
        title={title}
        style={submitting ? { backgroundColor: '#dbdbdb' } : undefined}
        actions={CurrentActions ? <CurrentActions /> : null}
      >
        <CurrentComponent {...componentProps} />
      </Modal>

      <PlacePayPaymentModal
        isOpen={isPlacePayOpen}
        paymentAccountId={selectedPaymentAccount?._id}
        // @ts-expect-error ts-migrate(2322) FIXME: Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
        payeeId={essensysPaymentSettings?.orgpayeeid}
        amount={
          (quote as any)?.data?.overage
            ? (quote as any)?.data?.overage?.finalPriceInCents
            : // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
              quote?.total + quote?.tax
        }
        description={content.name}
        onClose={() => {
          setIsPlacePayOpen(false);
          placePayRef.current.reject();
        }}
        onPaymentSuccess={data => {
          setIsPlacePayOpen(false);
          placePayRef.current.resolve(data);
        }}
        onPaymentFailed={err => {
          placePayRef.current.reject(err);
          setIsPlacePayOpen(false);
        }}
      />
    </>
  );
}
